import { IContactRespSchema } from '@monetize/types/app';
import { forwardRef, useMemo } from 'react';
import { contactSelectRenderItem } from '../../../../../components/Contacts/ContactSelect';
import { MCustomSelect } from '../../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { MainSearchInput } from '../../../../../components/Monetize/MCustomSelect/components/MainSearchInput';
import { CONTACTS } from '../../../../../constants';

interface QuoteContactsSelectInputProps {
  isDisabled?: boolean;
  /** Account whose contacts are searched server-side */
  accountId: string;
  /** Contact ids to filter out of returned pages (e.g. already-selected contacts) */
  excludeContactIds?: string[];
  onChange: (contact: IContactRespSchema) => void;
  onNewContact: () => void;
  canCreateContact: boolean;
}

const ADDITIONAL_SEARCH_PARAMS = {
  showInternal: 'false',
  status: 'ACTIVE',
  sort: 'fullName:asc',
  pageSize: '50',
} as const;

export const QuoteContactsSelectInput = forwardRef(
  (
    {
      isDisabled,
      accountId,
      excludeContactIds,
      onChange,
      onNewContact,
      canCreateContact,
    }: QuoteContactsSelectInputProps,
    ref,
  ) => {
    const excludeSet = useMemo(
      () => new Set(excludeContactIds || []),
      [excludeContactIds],
    );

    const transformDataFromApi = useMemo(
      () => (items: IContactRespSchema[]) =>
        (items || []).filter((c) => !excludeSet.has(c.id)),
      [excludeSet],
    );

    return (
      <MCustomSelect
        name="contacts"
        placeholder="Select Contact"
        endpoint={`/api/accounts/${accountId}/contacts`}
        additionalSearchParams={ADDITIONAL_SEARCH_PARAMS}
        itemTitle="fullName"
        itemValue="id"
        itemSearch="fullName"
        transformDataFromApi={transformDataFromApi}
        infiniteScrolling
        returnItem
        onChange={(contact: any) => onChange(contact as IContactRespSchema)}
        useMainInputAsSearchInput
        MainInputComponent={MainSearchInput}
        useHighlightedItemList
        showItemDivider
        inputProps={{
          autoComplete: 'off',
        }}
        prependItems={
          canCreateContact
            ? [
                {
                  item: CONTACTS.NEW_CONTACT,
                  isAction: true,
                  hasDivider: true,
                  onClick: ({ onClose }) => {
                    onClose && onClose();
                    onNewContact();
                  },
                },
              ]
            : []
        }
        renderItemContent={contactSelectRenderItem}
        isDisabled={isDisabled}
        ref={ref}
      />
    );
  },
);

export default QuoteContactsSelectInput;
